import React from 'react';
import { UncontrolledTooltip } from 'reactstrap';
import { useId } from 'js/hooks/useId';

import styles from './AnalysisUnitsGauge.module.scss';

function isUnlimited(usage) {
  return usage.remaining === 'unlimited' || usage.unit_limit === 'unlimited';
}

function formatRemaining(usage, usedPercent) {
  if (isUnlimited(usage)) {
    return 'Unlimited monthly analysis credits';
  }

  const remainingPercent = Math.max(0, 100 - usedPercent);
  return `${remainingPercent}% of monthly analysis credits remaining`;
}

function usedRatio(usage) {
  if (isUnlimited(usage)) {
    return 0;
  }

  const limit = Number(usage.unit_limit) || 0;
  if (limit <= 0) {
    return 1;
  }

  const used = Number(usage.units_used);
  if (Number.isFinite(used)) {
    return Math.min(1, Math.max(0, used / limit));
  }

  const remaining = Number(usage.remaining) || 0;
  return Math.min(1, Math.max(0, (limit - remaining) / limit));
}

function AnalysisUnitsGauge({ usage }) {
  const targetId = useId();

  if (!usage) {
    return null;
  }

  const ratio = usedRatio(usage);
  const percent = Math.round(ratio * 100);
  const tooltip = formatRemaining(usage, percent);

  return (
    <div id={targetId} className={styles.gauge} data-test="org-analysis-units">
      <span
        className={styles.track}
        role="meter"
        aria-label="Monthly analysis credits"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        aria-valuetext={tooltip}
      >
        <span className={styles.fill} style={{ width: `${percent}%` }} />
      </span>
      <UncontrolledTooltip
        target={targetId}
        placement="bottom"
        delay={200}
        className={styles.tooltip}
      >
        {tooltip}
      </UncontrolledTooltip>
    </div>
  );
}

export default AnalysisUnitsGauge;
